import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/site/Seo";
import { Spinner } from "@/admin/AdminLayout";

function Shell({ title, desc, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--ink)] p-4">
      <Seo title={`${title} | Rüzgar Özel Güvenlik`} noindex path="/admin/giris" />
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
        <img src="/assets/logo-shield.webp" alt="Rüzgar Özel Güvenlik" className="mx-auto h-16" />
        <h1 className="mt-4 text-center text-xl font-extrabold text-zinc-950">{title}</h1>
        {desc && <p className="mt-2 text-center text-sm text-zinc-500">{desc}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function useSetupStatus() {
  const [needs, setNeeds] = useState(null);
  useEffect(() => {
    api.get("/auth/setup-status").then((r) => setNeeds(r.data.needs_setup)).catch(() => setNeeds(false));
  }, []);
  return needs;
}

export function Login() {
  const { user, setUser } = useAuth();
  const needs = useSetupStatus();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to="/admin" replace />;
  if (needs === null) return <Spinner />;
  if (needs) return <Navigate to="/admin/kurulum" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/auth/login", f);
      setUser(r.data);
      nav("/admin");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell title="Yönetim Paneli Girişi">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><label className="field-label">E-posta</label><input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field-input" autoComplete="username" data-testid="login-email" /></div>
        <div><label className="field-label">Şifre</label><input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="field-input" autoComplete="current-password" data-testid="login-password" /></div>
        {err && <p className="rounded bg-red-50 p-3 text-sm text-red-700" data-testid="login-error">{err}</p>}
        <button disabled={loading} className="btn-red w-full" data-testid="login-submit-btn">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Giriş Yap</button>
      </form>
    </Shell>
  );
}

export function Setup() {
  const { setUser } = useAuth();
  const needs = useSetupStatus();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", password2: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  if (needs === null) return <Spinner />;
  if (!needs) return <Navigate to="/admin/giris" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 10) return setErr("Şifre en az 10 karakter olmalıdır.");
    if (f.password !== f.password2) return setErr("Şifreler eşleşmiyor.");
    setLoading(true);
    try {
      const r = await api.post("/auth/setup", { name: f.name, email: f.email, password: f.password });
      setUser(r.data);
      nav("/admin");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };
  const inp = (k, label, type = "text") => (
    <div><label className="field-label">{label}</label><input type={type} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field-input" data-testid={`setup-${k}`} /></div>
  );
  return (
    <Shell title="İlk Yönetici Kurulumu" desc="Bu ekran yalnızca bir kez görünür. Yönetici hesabı oluşturulduktan sonra kapanır.">
      <form onSubmit={submit} className="space-y-4" data-testid="setup-form">
        {inp("name", "Ad Soyad")}
        {inp("email", "E-posta", "email")}
        {inp("password", "Şifre (en az 10 karakter)", "password")}
        {inp("password2", "Şifre (tekrar)", "password")}
        {err && <p className="rounded bg-red-50 p-3 text-sm text-red-700" data-testid="setup-error">{err}</p>}
        <button disabled={loading} className="btn-red w-full" data-testid="setup-submit-btn"><ShieldCheck className="h-4 w-4" /> Yönetici Hesabını Oluştur</button>
      </form>
    </Shell>
  );
}
